import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, use } from "react";
import { axios } from "@/lib/axios";
import { queryKeys } from "@/lib/query-keys";

type FeatureFlagValues = {
  pilotSelfServeLocked: boolean;
  fixedJobTemplatesEnabled: boolean;
};

const defaultFeatureFlagValues: FeatureFlagValues = {
  pilotSelfServeLocked: false,
  fixedJobTemplatesEnabled: false,
};

const FeatureFlagsContext = createContext<FeatureFlagValues>(
  defaultFeatureFlagValues
);

type PilotFlagProviderProps = {
  children: ReactNode;
  pilotSelfServeLocked?: boolean;
  fixedJobTemplatesEnabled?: boolean;
};

export function PilotFlagProvider({
  children,
  pilotSelfServeLocked,
  fixedJobTemplatesEnabled,
}: PilotFlagProviderProps) {
  const needsFetch =
    pilotSelfServeLocked === undefined ||
    fixedJobTemplatesEnabled === undefined;

  const { data } = useQuery({
    queryKey: queryKeys.flags.all,
    queryFn: async () => {
      const response = await axios.get<FeatureFlagValues>("/flags");
      return response.data;
    },
    enabled: needsFetch,
    staleTime: 60_000,
  });

  const value: FeatureFlagValues = {
    pilotSelfServeLocked:
      pilotSelfServeLocked ?? data?.pilotSelfServeLocked ?? false,
    fixedJobTemplatesEnabled:
      fixedJobTemplatesEnabled ?? data?.fixedJobTemplatesEnabled ?? false,
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function usePilotSelfServeLocked(): boolean {
  return use(FeatureFlagsContext).pilotSelfServeLocked;
}

export function useFixedJobTemplatesEnabled(): boolean {
  return use(FeatureFlagsContext).fixedJobTemplatesEnabled;
}
